import { STICKY_BANNER_RULE_TYPES } from "../configs/stickyBanner/stickyBannerConfig.types";
import {
  getMessagePayloadById,
  resolveTriggerPayload,
  resolveCarouselMessageIds
} from "../configs/stickyBanner/common/stickyBannerResolver";

function rulePriority(rule) {
  return Number.isFinite(rule?.priority) ? rule.priority : 0;
}

function matchesScoreRule(rule, ctx) {
  if (ctx.scorePercent == null) return false;
  const min = rule.minPercent ?? 0;
  const max = rule.maxPercent ?? 100;
  return ctx.scorePercent >= min && ctx.scorePercent <= max;
}

function matchesSubjectRule(rule, ctx) {
  if (!rule.subjectKey || !ctx.subjectSections?.length) return false;
  const key = String(rule.subjectKey).toLowerCase();
  return ctx.subjectSections.some((s) => {
    if (!s.sectionName) return false;
    if (!s.sectionName.toLowerCase().includes(key)) return false;
    const m = s.marksObtained;
    if (!Number.isFinite(m)) return false;
    const minM = rule.minMark ?? -Infinity;
    const maxM = rule.maxMark ?? Infinity;
    return m >= minM && m <= maxM;
  });
}

function matchesConditionalRule(rule, ctx) {
  switch (rule.type) {
    case STICKY_BANNER_RULE_TYPES.SCORE:
      return matchesScoreRule(rule, ctx);
    case STICKY_BANNER_RULE_TYPES.SUBJECT_MIN_MARK:
      return matchesSubjectRule(rule, ctx);
    default:
      return false;
  }
}

/**
 * Picks highest-priority matching conditional rule (score / subject only), else explicit default rule.
 * @param {object} config
 * @param {object} ctx from buildStickyBannerContext
 */
export function resolveConditionalBannerRule(config, ctx) {
  const rules = config?.messageRules;
  if (!Array.isArray(rules) || rules.length === 0) {
    return null;
  }

  const specific = rules
    .filter((r) => r && r.type !== STICKY_BANNER_RULE_TYPES.DEFAULT && matchesConditionalRule(r, ctx))
    .sort((a, b) => rulePriority(b) - rulePriority(a));

  if (specific.length) {
    return specific[0];
  }

  const defaults = rules
    .filter((r) => r && r.type === STICKY_BANNER_RULE_TYPES.DEFAULT)
    .sort((a, b) => rulePriority(b) - rulePriority(a));

  return defaults[0] ?? null;
}

/**
 * Among scroll triggers, pick highest priority whose conditions are active.
 * @param {object} config
 * @param {{ activeScrollTriggerIds: Set<string>; scrollRatio: number }} scrollState
 */
export function resolveScrollBannerOverride(config, scrollState) {
  const triggers = [...(config?.scrollTriggers ?? [])];
  if (!triggers.length) return null;

  const { activeScrollTriggerIds, scrollRatio } = scrollState;
  const ratio = Number.isFinite(scrollRatio) ? scrollRatio : 0;

  const matched = [];

  for (const t of triggers) {
    if (!t?.id) continue;
    if (t.type === STICKY_BANNER_RULE_TYPES.SCROLL_ELEMENT) {
      if (activeScrollTriggerIds?.has(t.id)) {
        matched.push(t);
      }
    } else if (t.type === STICKY_BANNER_RULE_TYPES.SCROLL_PERCENT) {
      const min = t.min ?? 0;
      const max = t.max ?? 1;
      if (ratio >= min && ratio < max) {
        matched.push(t);
      }
    }
  }

  if (!matched.length) return null;
  matched.sort((a, b) => rulePriority(b) - rulePriority(a));
  return matched[0];
}

/**
 * Milestone scroll: phase 1 = default; phase 2+ matched from scrollMilestones by `order`.
 * Supports `messageId` (preferred) or legacy `messageParts` on milestone rows.
 */
export function resolveMilestoneDisplay(config, phase) {
  const milestones = config?.scrollMilestones ?? [];
  const match = milestones.find((m) => Number(m?.order) === Number(phase));
  if (match?.messageId) {
    const payload = getMessagePayloadById(match.messageId, config?.defaultCtaText, "scroll-milestone");
    if (payload) {
      return { ...payload, matchedRuleId: match.id };
    }
  }
  if (match?.messageParts?.length) {
    return {
      messageParts: match.messageParts,
      ctaText: match.ctaText ?? config.defaultCtaText ?? "Book a Session",
      matchedRuleId: match.id,
      source: "scroll-milestone"
    };
  }

  const fallback = config?.defaultMessageParts;
  return {
    messageParts: Array.isArray(fallback) && fallback.length ? fallback : [{ kind: "text", content: "" }],
    ctaText: config?.defaultCtaText ?? "Book a Session",
    matchedRuleId: "default",
    source: "default"
  };
}

export function resolveCarouselDisplay(config, slideIndex) {
  const fromIds = resolveCarouselMessageIds(config, slideIndex);
  if (fromIds) {
    return fromIds;
  }

  const slides = config?.carouselMessages ?? [];
  if (!slides.length) {
    return null;
  }
  const i = ((slideIndex % slides.length) + slides.length) % slides.length;
  const slide = slides[i];
  if (!slide?.messageParts?.length) {
    return null;
  }
  return {
    messageParts: slide.messageParts,
    ctaText: slide.ctaText ?? config.defaultCtaText ?? "Book a Session",
    matchedRuleId: slide.id ?? `carousel-${i}`,
    source: "carousel"
  };
}

/**
 * Scroll overrides beat conditional rules when present; else conditional; else default.
 */
export function resolveStickyBannerDisplay(config, ctx, scrollState) {
  const matchedTrigger = resolveScrollBannerOverride(config, scrollState);
  if (matchedTrigger) {
    const expanded = resolveTriggerPayload(matchedTrigger, config);
    if (expanded?.messageParts?.length) {
      return { ...expanded, matchedRuleId: matchedTrigger.id };
    }
  }

  const conditional = resolveConditionalBannerRule(config, ctx);
  if (conditional?.messageParts?.length) {
    return {
      messageParts: conditional.messageParts,
      ctaText: conditional.ctaText ?? config.defaultCtaText ?? "Book a Session",
      matchedRuleId: conditional.id,
      source: "conditional"
    };
  }

  if (config.defaultMessageId) {
    const payload = getMessagePayloadById(config.defaultMessageId, config.defaultCtaText, "config-default");
    if (payload) {
      return { ...payload, matchedRuleId: config.defaultMessageId };
    }
  }

  const fallback = config?.defaultMessageParts;
  return {
    messageParts: Array.isArray(fallback) && fallback.length ? fallback : [{ kind: "text", content: "" }],
    ctaText: config?.defaultCtaText ?? "Book a Session",
    matchedRuleId: "default",
    source: "default"
  };
}
